import React, { useState, useEffect } from 'react';
import { Campaign, CampaignQuestion } from '../../progress/types';
import { Timestamp } from 'firebase/firestore';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import '../styles/CampaignEditor.css';

interface CampaignEditorProps {
  campaign: Campaign | null;
  onSave: (campaignData: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}

const CampaignEditor: React.FC<CampaignEditorProps> = ({
  campaign,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    name: '',
    active: true,
    dateRange: {
      start: '',
      end: ''
    },
    squadIds: [] as string[],
    questions: [] as CampaignQuestion[]
  });

  const [availableSquads, setAvailableSquads] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadSquads();
    if (campaign) {
      setFormData({
        name: campaign.name,
        active: campaign.active,
        dateRange: {
          start: campaign.dateRange.start.toDate().toISOString().slice(0, 16),
          end: campaign.dateRange.end.toDate().toISOString().slice(0, 16)
        },
        squadIds: campaign.squadIds,
        questions: campaign.questions
      });
    }
  }, [campaign]);

  const loadSquads = async () => {
    try {
      const squadsSnapshot = await getDocs(collection(db, 'squads'));
      const squadsList = squadsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.title || data.displayName || data.name || `Squad ${doc.id}`
        };
      });
      setAvailableSquads(squadsList);
    } catch (error) {
      console.error('Error loading squads:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Campaign name is required';
    }

    if (!formData.dateRange.start) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.dateRange.end) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.dateRange.start && formData.dateRange.end) {
      const start = new Date(formData.dateRange.start);
      const end = new Date(formData.dateRange.end);
      if (start >= end) {
        newErrors.dateRange = 'End date must be after start date';
      }
    }

    if (formData.squadIds.length === 0) {
      newErrors.squads = 'At least one squad must be selected';
    }

    if (formData.questions.length === 0) {
      newErrors.questions = 'At least one question is required';
    }

    // Validate questions
    formData.questions.forEach((question, index) => {
      if (!question.prompt.trim()) {
        newErrors[`question-${index}-prompt`] = 'Question prompt is required';
      }
      if (question.type === 'multiple_choice' && (!question.options || question.options.length < 2)) {
        newErrors[`question-${index}-options`] = 'Multiple choice questions need at least 2 options';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const campaignData: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'> = {
      name: formData.name.trim(),
      active: formData.active,
      dateRange: {
        start: Timestamp.fromDate(new Date(formData.dateRange.start)),
        end: Timestamp.fromDate(new Date(formData.dateRange.end))
      },
      squadIds: formData.squadIds,
      questions: formData.questions
    };

    onSave(campaignData);
  };

  const handleAddQuestion = () => {
    const newQuestion: CampaignQuestion = {
      id: `q${Date.now()}`,
      questionId: `q${Date.now()}`,
      type: 'multiple_choice',
      prompt: '',
      options: ['', '']
    };

    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const handleUpdateQuestion = (index: number, updates: Partial<CampaignQuestion>) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === index ? { ...q, ...updates } : q)
    }));
  };

  const handleRemoveQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleAddOption = (questionIndex: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { ...q, options: [...(q.options || []), ''] }
          : q
      )
    }));
  };

  const handleUpdateOption = (questionIndex: number, optionIndex: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options?.map((opt, oi) => oi === optionIndex ? value : opt)
            }
          : q
      )
    }));
  };

  const handleRemoveOption = (questionIndex: number, optionIndex: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options?.filter((_, oi) => oi !== optionIndex)
            }
          : q
      )
    }));
  };

  return (
    <div className="campaign-editor">
      <div className="campaign-editor__header">
        <div className="campaign-editor__header-nav">
          <button
            onClick={onCancel}
            className="campaign-editor__back-button"
          >
            ←
          </button>
          <h2 className="campaign-editor__title">
            {campaign ? 'Edit Campaign' : 'Create New Campaign'}
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="campaign-editor__section">
          <h3 className="campaign-editor__section-title">Basic Information</h3>
          
          {/* Campaign Name */}
          <div className="campaign-editor__field">
            <label className="campaign-editor__label">
              Campaign Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter campaign name..."
              className={`campaign-editor__input ${errors.name ? 'campaign-editor__input--error' : ''}`}
            />
            {errors.name && <div className="campaign-editor__error">{errors.name}</div>}
          </div>

          {/* Active Status */}
          <div className="campaign-editor__field">
            <label className="campaign-editor__label--inline">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                className="campaign-editor__checkbox"
              />
              Campaign Active
            </label>
            <div className="campaign-editor__help-text">
              Only active campaigns will be shown to students
            </div>
          </div>

          {/* Date Range */}
          <div className="campaign-editor__date-row">
            <div className="campaign-editor__date-field">
              <label className="campaign-editor__label">
                Start Date *
              </label>
              <input
                type="datetime-local"
                value={formData.dateRange.start}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  dateRange: { ...prev.dateRange, start: e.target.value }
                }))}
                className={`campaign-editor__input ${errors.startDate ? 'campaign-editor__input--error' : ''}`}
              />
              {errors.startDate && <div className="campaign-editor__error">{errors.startDate}</div>}
            </div>

            <div className="campaign-editor__date-field">
              <label className="campaign-editor__label">
                End Date *
              </label>
              <input
                type="datetime-local"
                value={formData.dateRange.end}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  dateRange: { ...prev.dateRange, end: e.target.value }
                }))}
                className={`campaign-editor__input ${errors.endDate ? 'campaign-editor__input--error' : ''}`}
              />
              {errors.endDate && <div className="campaign-editor__error">{errors.endDate}</div>}
            </div>
          </div>

          {errors.dateRange && (
            <div className="campaign-editor__error" style={{ marginBottom: '1.5rem' }}>{errors.dateRange}</div>
          )}

          {/* Squad Selection */}
          <div className="campaign-editor__field">
            <div className="campaign-editor__squad-header">
              <label className="campaign-editor__label">
                Target Squads *
              </label>
              <button
                type="button"
                onClick={() => {
                  const allSelected = formData.squadIds.length === availableSquads.length;
                  setFormData(prev => ({
                    ...prev,
                    squadIds: allSelected ? [] : availableSquads.map(squad => squad.id)
                  }));
                }}
                className={`campaign-editor__button ${
                  formData.squadIds.length === availableSquads.length
                    ? 'campaign-editor__button--select-all'
                    : 'campaign-editor__button--primary'
                }`}
              >
                {formData.squadIds.length === availableSquads.length ? 'Deselect All' : 'Select All Squads'}
              </button>
            </div>
            <div className={`campaign-editor__squad-container ${errors.squads ? 'campaign-editor__squad-container--error' : ''}`}>
              {availableSquads.map(squad => (
                <label
                  key={squad.id}
                  className="campaign-editor__squad-item"
                >
                  <input
                    type="checkbox"
                    checked={formData.squadIds.includes(squad.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({
                          ...prev,
                          squadIds: [...prev.squadIds, squad.id]
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          squadIds: prev.squadIds.filter(id => id !== squad.id)
                        }));
                      }
                    }}
                  />
                  {squad.name}
                </label>
              ))}
            </div>
            {errors.squads && <div className="campaign-editor__error">{errors.squads}</div>}
          </div>
        </div>

        {/* Questions Section */}
        <div className="campaign-editor__section">
          <div className="campaign-editor__questions-header">
            <h3 className="campaign-editor__section-title">Questions</h3>
            <button
              type="button"
              onClick={handleAddQuestion}
              className="campaign-editor__button campaign-editor__button--primary"
            >
              ➕ Add Question
            </button>
          </div>

          {formData.questions.map((question, questionIndex) => (
            <div
              key={questionIndex}
              className="campaign-editor__question-card"
            >
              <div className="campaign-editor__question-header">
                <h4 className="campaign-editor__question-title">Question {questionIndex + 1}</h4>
                <button
                  type="button"
                  onClick={() => handleRemoveQuestion(questionIndex)}
                  className="campaign-editor__button campaign-editor__button--danger campaign-editor__button--small"
                >
                  Remove
                </button>
              </div>

              {/* Question Type */}
              <div className="campaign-editor__field">
                <label className="campaign-editor__label">
                  Question Type
                </label>
                <select
                  value={question.type}
                  onChange={(e) => handleUpdateQuestion(questionIndex, {
                    type: e.target.value as 'multiple_choice' | 'text',
                    options: e.target.value === 'multiple_choice' ? ['', ''] : undefined
                  })}
                  className="campaign-editor__select"
                >
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="text">Text Response</option>
                </select>
              </div>

              {/* Question Prompt */}
              <div className="campaign-editor__field">
                <label className="campaign-editor__label">
                  Question Text
                </label>
                <textarea
                  value={question.prompt}
                  onChange={(e) => handleUpdateQuestion(questionIndex, { prompt: e.target.value })}
                  placeholder="Enter your question..."
                  className={`campaign-editor__textarea ${errors[`question-${questionIndex}-prompt`] ? 'campaign-editor__textarea--error' : ''}`}
                />
                {errors[`question-${questionIndex}-prompt`] && (
                  <div className="campaign-editor__error campaign-editor__error--small">
                    {errors[`question-${questionIndex}-prompt`]}
                  </div>
                )}
              </div>

              {/* Multiple Choice Options */}
              {question.type === 'multiple_choice' && (
                <div>
                  <div className="campaign-editor__options-header">
                    <label className="campaign-editor__label">Answer Options</label>
                    <button
                      type="button"
                      onClick={() => handleAddOption(questionIndex)}
                      className="campaign-editor__button campaign-editor__button--secondary campaign-editor__button--small"
                    >
                      Add Option
                    </button>
                  </div>
                  
                  {question.options?.map((option, optionIndex) => (
                    <div key={optionIndex} className="campaign-editor__option-row">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => handleUpdateOption(questionIndex, optionIndex, e.target.value)}
                        placeholder={`Option ${optionIndex + 1}`}
                        className="campaign-editor__option-input"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(questionIndex, optionIndex)}
                        disabled={(question.options?.length || 0) <= 2}
                        className={`campaign-editor__button campaign-editor__button--remove-option ${
                          (question.options?.length || 0) <= 2
                            ? 'campaign-editor__button--cancel'
                            : 'campaign-editor__button--danger'
                        }`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  
                  {errors[`question-${questionIndex}-options`] && (
                    <div className="campaign-editor__error campaign-editor__error--small">
                      {errors[`question-${questionIndex}-options`]}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {errors.questions && (
            <div className="campaign-editor__error">{errors.questions}</div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="campaign-editor__actions">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="campaign-editor__button campaign-editor__button--cancel"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className={`campaign-editor__button ${
              loading
                ? 'campaign-editor__button--loading'
                : 'campaign-editor__button--primary'
            }`}
          >
            {loading ? 'Saving...' : campaign ? 'Update Campaign' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignEditor;